import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useBoomerForm } from "@/form/boomer/hook";
import { useValidator } from "@/form/common/validator";
import type { AcceptEmployeeInviteInput, Invitation } from "@/lib/backend/api";
import { Routes } from "@/lib/router";

export type AcceptEmployeeInviteFormProps = {
  id?: string;
  onSubmit: (values: AcceptEmployeeInviteInput) => Promise<void>;
  disabled?: boolean;
  invitation?: Invitation;
};

export function AcceptInviteEmployeeForm({
  disabled,
  invitation,
  onSubmit,
}: AcceptEmployeeInviteFormProps) {
  const params = Routes.AuthAcceptInvite.useParams();
  const token = params.token ?? "";
  const validator = useValidator();
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const [form, { Element: FormElement }] = useBoomerForm({
    defaultValues: {
      firstname: invitation?.firstname ?? "",
      lastname: invitation?.lastname ?? "",
      token,
      password: "",
      repeatPassword: "",
      phone: "+31",
    },
    validators: {
      onSubmit: ({ value }) => {
        if (!value.token) {
          return {
            form: "Token is required",
            fields: {
              token: "Required",
            },
          };
        }
        if (value.password !== value.repeatPassword) {
          return {
            form: "Wachtwoorden komen niet overeen",
            fields: {
              repeatPassword: "Wachtwoorden komen niet overeen",
            },
          };
        }
      },
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value);
    },
  });

  return (
    <FormElement className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <form.AppField
          name="firstname"
          validators={{
            onBlur: validator.required({ name: "firstname" }),
            onSubmit: validator.required({ name: "firstname" }),
          }}
          children={(field) => <field.InputField required disabled={disabled} withPlaceholder />}
        />
        <form.AppField
          name="lastname"
          validators={{
            onBlur: validator.required({ name: "lastname" }),
            onSubmit: validator.required({ name: "lastname" }),
          }}
          children={(field) => <field.InputField required disabled={disabled} withPlaceholder />}
        />
        <form.AppField
          name="password"
          validators={{
            onBlur: validator.required({ name: "password" }),
            onSubmit: validator.required({ name: "password" }),
          }}
          children={(field) => (
            <div className="relative flex items-center">
              <field.InputField
                type={showPassword ? "text" : "password"}
                required
                disabled={disabled}
                withPlaceholder
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[40px] z-10 text-brand-900 hover:text-brand-900"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          )}
        />
        <form.AppField
          name="repeatPassword"
          validators={{
            onBlur: validator.required({ name: "repeatPassword" }),
            onSubmit: validator.required({ name: "repeatPassword" }),
          }}
          children={(field) => (
            <div className="relative flex items-center">
              <field.InputField
                type={showRepeatPassword ? "text" : "password"}
                required
                disabled={disabled}
                withPlaceholder
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                className="absolute right-3 top-[40px] z-10 text-brand-900 hover:text-brand-900"
                tabIndex={-1}
              >
                {showRepeatPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          )}
        />
        <form.AppField
          name="phone"
          validators={{
            onBlur: validator.phone({ schema: validator.required({ name: "phone" }) }),
            onSubmit: validator.phone({ schema: validator.required({ name: "phone" }) }),
          }}
          children={(field) => (
            <field.PhoneField required disabled={disabled} defaultCountry="NL" />
          )}
        />
      </div>

      <form.Subscribe
        selector={(state) => [state.canSubmit, state.isSubmitting]}
        children={([canSubmit, isSubmitting]) => (
          <button
            type="submit"
            disabled={!canSubmit || isSubmitting}
            className="w-full rounded-lg bg-brand-500 px-4 py-3 text-base font-medium text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? "Opslaan..." : "Opslaan"}
          </button>
        )}
      />
    </FormElement>
  );
}
